<template>
  <AdminLayout>
    <div class="max-w-6xl mx-auto">
      <!-- 헤더 -->
      <div class="flex items-center gap-3 mb-6">
        <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-slate-800">스플래시 광고 관리</h1>
          <p class="text-sm text-slate-500">스플래시 광고 신청을 승인하거나 반려합니다</p>
        </div>
      </div>

      <!-- 탭 -->
      <div class="flex gap-2 mb-6">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          @click="currentTab = tab.value"
          :class="[
            'px-4 py-2 rounded-xl font-medium transition',
            currentTab === tab.value
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
              : 'bg-white text-slate-600 hover:bg-slate-50'
          ]"
        >
          {{ tab.label }}
          <span v-if="tab.value === 'pending' && pendingCount > 0" class="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
            {{ pendingCount }}
          </span>
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-slate-500">로딩 중...</p>
      </div>

      <!-- 빈 상태 -->
      <div v-else-if="filteredAds.length === 0" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-slate-600 mb-2">신청 내역이 없습니다</h3>
        <p class="text-slate-400">해당 상태의 스플래시 광고 신청이 없습니다.</p>
      </div>

      <!-- 신청 목록 -->
      <div v-else class="space-y-4">
        <div
          v-for="ad in filteredAds"
          :key="ad.id"
          class="bg-white rounded-2xl overflow-hidden shadow-sm"
        >
          <div class="flex">
            <!-- 이미지 -->
            <div class="w-40 h-56 bg-slate-100 flex-shrink-0">
              <img
                :src="ad.imageUrl"
                :alt="ad.title"
                class="w-full h-full object-cover cursor-pointer"
                @click="openImageModal(ad.imageUrl)"
              />
            </div>

            <!-- 정보 -->
            <div class="flex-1 p-5">
              <div class="flex items-start justify-between mb-3">
                <div>
                  <div class="flex items-center gap-2 mb-2">
                    <span :class="[
                      'px-2 py-0.5 text-xs font-semibold rounded-full',
                      getStatusClass(ad.status)
                    ]">{{ getStatusText(ad.status) }}</span>
                    <span class="text-xs text-slate-400">{{ ad.timeSlot?.icon }} {{ ad.timeSlot?.label }} ({{ ad.timeSlot?.start }}-{{ ad.timeSlot?.end }})</span>
                  </div>
                  <h3 class="text-lg font-semibold text-slate-800">{{ ad.title }}</h3>
                  <p v-if="ad.subtitle" class="text-sm text-slate-500">{{ ad.subtitle }}</p>
                </div>
              </div>

              <!-- 신청자 정보 -->
              <div class="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p class="text-xs text-slate-400 mb-1">신청자</p>
                  <p class="text-sm font-medium text-slate-700">{{ ad.partnerDisplayName }}</p>
                  <p class="text-xs text-slate-400">{{ ad.partnerEmail }}</p>
                </div>
                <div>
                  <p class="text-xs text-slate-400 mb-1">연결 캠페인</p>
                  <p class="text-sm font-medium text-slate-700">{{ ad.propertyName }}</p>
                  <p class="text-xs text-slate-400">{{ ad.campaignCode }}</p>
                </div>
              </div>

              <!-- 통계 (승인된 경우) -->
              <div v-if="ad.status === 'approved'" class="flex gap-6 mb-4 p-3 bg-slate-50 rounded-lg">
                <div class="text-center">
                  <p class="text-xl font-bold text-cyan-600">{{ ad.impressions || 0 }}</p>
                  <p class="text-xs text-slate-400">노출</p>
                </div>
                <div class="text-center">
                  <p class="text-xl font-bold text-cyan-600">{{ ad.clicks || 0 }}</p>
                  <p class="text-xs text-slate-400">클릭</p>
                </div>
                <div class="text-center">
                  <p class="text-xl font-bold text-cyan-600">{{ getClickRate(ad) }}%</p>
                  <p class="text-xs text-slate-400">클릭률</p>
                </div>
              </div>

              <!-- 날짜 -->
              <div class="text-xs text-slate-400 mb-4">
                신청일: {{ formatDate(ad.createdAt) }}
                <span v-if="ad.approvedAt"> | 승인일: {{ formatDate(ad.approvedAt) }}</span>
                <span v-if="ad.rejectedAt"> | 반려일: {{ formatDate(ad.rejectedAt) }}</span>
              </div>

              <!-- 액션 버튼 -->
              <div v-if="ad.status === 'pending'" class="flex gap-2">
                <button
                  @click="approveAd(ad)"
                  :disabled="processing"
                  class="flex-1 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition disabled:opacity-50"
                >
                  승인
                </button>
                <button
                  @click="openRejectModal(ad)"
                  :disabled="processing"
                  class="flex-1 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition disabled:opacity-50"
                >
                  반려
                </button>
              </div>

              <!-- 반려 사유 -->
              <div v-if="ad.status === 'rejected' && ad.rejectReason" class="p-3 bg-red-50 rounded-lg">
                <p class="text-sm text-red-600">
                  <span class="font-medium">반려 사유:</span> {{ ad.rejectReason }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 이미지 모달 -->
      <Teleport to="body">
        <Transition name="modal">
          <div v-if="showImageModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" @click="showImageModal = false">
            <img :src="modalImageUrl" class="max-w-full max-h-full rounded-lg" @click.stop />
          </div>
        </Transition>
      </Teleport>

      <!-- 반려 모달 -->
      <Teleport to="body">
        <Transition name="modal">
          <div v-if="showRejectModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div class="bg-white rounded-2xl p-6 w-full max-w-md" @click.stop>
              <h3 class="text-lg font-semibold text-slate-800 mb-4">반려 사유 입력</h3>
              <textarea
                v-model="rejectReason"
                class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows="3"
                placeholder="반려 사유를 입력하세요"
              ></textarea>
              <div class="flex gap-2 mt-4">
                <button
                  @click="showRejectModal = false"
                  class="flex-1 py-2 border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-50 transition"
                >
                  취소
                </button>
                <button
                  @click="confirmReject"
                  :disabled="!rejectReason.trim() || processing"
                  class="flex-1 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition disabled:opacity-50"
                >
                  반려하기
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useSplashAd } from '@/composables/main/useSplashAd'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'SplashAdApplicationsView',
  components: {
    AdminLayout
  },
  setup() {
    const { getAllSplashAds, updateSplashAdStatus } = useSplashAd()

    const loading = ref(true)
    const processing = ref(false)
    const splashAds = ref([])
    const currentTab = ref('pending')

    const showImageModal = ref(false)
    const modalImageUrl = ref('')

    const showRejectModal = ref(false)
    const rejectReason = ref('')
    const selectedAd = ref(null)

    const tabs = [
      { label: '대기 중', value: 'pending' },
      { label: '승인됨', value: 'approved' },
      { label: '반려됨', value: 'rejected' },
      { label: '전체', value: 'all' }
    ]

    const pendingCount = computed(() => {
      return splashAds.value.filter(ad => ad.status === 'pending').length
    })

    const filteredAds = computed(() => {
      if (currentTab.value === 'all') {
        return splashAds.value
      }
      return splashAds.value.filter(ad => ad.status === currentTab.value)
    })

    const loadData = async () => {
      try {
        loading.value = true
        splashAds.value = await getAllSplashAds()
      } catch (error) {
        console.error('데이터 로드 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const getStatusClass = (status) => {
      switch (status) {
        case 'pending': return 'bg-yellow-100 text-yellow-700'
        case 'approved': return 'bg-green-100 text-green-700'
        case 'rejected': return 'bg-red-100 text-red-700'
        default: return 'bg-slate-100 text-slate-600'
      }
    }

    const getStatusText = (status) => {
      switch (status) {
        case 'pending': return '대기 중'
        case 'approved': return '승인됨'
        case 'rejected': return '반려됨'
        default: return status
      }
    }

    const getClickRate = (ad) => {
      if (!ad.impressions || ad.impressions === 0) return '0.0'
      return ((ad.clicks || 0) / ad.impressions * 100).toFixed(1)
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
    }

    const openImageModal = (url) => {
      modalImageUrl.value = url
      showImageModal.value = true
    }

    const approveAd = async (ad) => {
      if (!confirm('이 스플래시 광고를 승인하시겠습니까?')) return

      try {
        processing.value = true
        await updateSplashAdStatus(ad.id, 'approved')
        await loadData()
        alert('승인되었습니다.')
      } catch (error) {
        console.error('승인 실패:', error)
        alert('승인에 실패했습니다.')
      } finally {
        processing.value = false
      }
    }

    const openRejectModal = (ad) => {
      selectedAd.value = ad
      rejectReason.value = ''
      showRejectModal.value = true
    }

    const confirmReject = async () => {
      if (!rejectReason.value.trim() || !selectedAd.value) return

      try {
        processing.value = true
        await updateSplashAdStatus(selectedAd.value.id, 'rejected', rejectReason.value.trim())
        showRejectModal.value = false
        await loadData()
        alert('반려되었습니다.')
      } catch (error) {
        console.error('반려 실패:', error)
        alert('반려에 실패했습니다.')
      } finally {
        processing.value = false
      }
    }

    onMounted(loadData)

    return {
      loading,
      processing,
      splashAds,
      currentTab,
      tabs,
      pendingCount,
      filteredAds,
      showImageModal,
      modalImageUrl,
      showRejectModal,
      rejectReason,
      getStatusClass,
      getStatusText,
      getClickRate,
      formatDate,
      openImageModal,
      approveAd,
      openRejectModal,
      confirmReject
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
